import { useState, useEffect, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from "react-hook-form";
import { Modal, Toast, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import Api from '../Auth/Api';
import { decodeJWT } from '../Components/jwtUtils';
import DropdownMenu from '../Components/DropdownMenu';
import { customStyles } from '../utils/selectStyles';
import { entrarNoContextoEmpresa } from '../utils/superAdminContexto';

const maskCNPJ = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    return digits
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
};

const maskTelefone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 10) {
        return digits.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').trim().replace(/[^0-9()-]\s*$/, '').trimEnd();
    }
    return digits.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

function ListaEmpresas() {
    const navigate = useNavigate();
    const token = JSON.parse(localStorage.getItem("user_token"));
    const empresaAtual = decodeJWT(token)?.empresa_id ?? null;

    const [empresas, setEmpresas] = useState([]);
    const [filtro, setFiltro] = useState('');
    const [showModal, setShowModal] = useState({ show: false, empresa: null });
    const [showConfirm, setShowConfirm] = useState({ show: false, empresa: null, acao: null });
    // Exclusão definitiva: etapa 1 = aviso, etapa 2 = digitar o nome da empresa
    const [showExcluir, setShowExcluir] = useState({ show: false, empresa: null, etapa: 1 });
    const [confirmacaoNome, setConfirmacaoNome] = useState('');
    const [excluindo, setExcluindo] = useState(false);
    const [showModalAdmin, setShowModalAdmin] = useState({ show: false, empresa: null });
    const [showSenha, setShowSenha] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toast, setToast] = useState({});
    const { register, handleSubmit, reset, control, formState: { errors } } = useForm();
    const { register: registerAdmin, handleSubmit: handleSubmitAdmin, reset: resetAdmin, formState: { errors: errorsAdmin } } = useForm();

    const totalEmpresas = empresas.length;
    const ativas = empresas.filter(e => e.status === 'ativo').length;
    const inativas = empresas.filter(e => e.status !== 'ativo').length;

    const fetchEmpresas = useCallback(async () => {
        try {
            const response = await Api.get('api/empresas', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEmpresas(response.data);
        } catch (error) {
            console.error(error?.response?.data?.message || error);
        }
    }, [token]);

    useEffect(() => { fetchEmpresas(); }, [fetchEmpresas]);

    const statusOptions = [
        { value: 'ativo', label: 'Ativo' },
        { value: 'inativo', label: 'Inativo' },
    ];

    const abrirNova = () => {
        reset({ nome: '', cnpj: '', telefone: '', nome_responsavel: '', endereco: '', status: statusOptions[0] });
        setShowModal({ show: true, empresa: null });
    };

    const abrirEdicao = (empresa) => {
        reset({
            nome: empresa.nome,
            cnpj: empresa.cnpj || '',
            telefone: empresa.telefone || '',
            nome_responsavel: empresa.nome_responsavel || '',
            endereco: empresa.endereco || '',
            status: statusOptions.find(o => o.value === empresa.status) ?? statusOptions[0],
        });
        setShowModal({ show: true, empresa });
    };

    const onSubmit = async (data) => {
        try {
            const url = showModal.empresa ? `api/empresas/${showModal.empresa.id}` : 'api/empresas';
            const payload = { ...data, status: data.status?.value ?? 'ativo' };
            const response = await Api.post(url, payload, { headers: { Authorization: `Bearer ${token}` } });
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            setShowModal({ show: false, empresa: null });
            fetchEmpresas();
        } catch (error) {
            setToast({ header: 'Ops!', body: error?.response?.data?.message || 'Erro ao salvar empresa.', type: 'toast-error' });
        }
        setShowToast(true);
    };

    const confirmarAcao = (empresa, acao) => {
        setShowConfirm({ show: true, empresa, acao });
    };

    const executarAcao = async () => {
        const { empresa, acao } = showConfirm;
        setShowConfirm({ show: false, empresa: null, acao: null });
        try {
            if (acao === 'inativar') {
                const response = await Api.delete(`api/empresas/${empresa.id}`, { headers: { Authorization: `Bearer ${token}` } });
                setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            } else if (acao === 'ativar') {
                const response = await Api.post(`api/empresas/${empresa.id}`, { nome: empresa.nome, cnpj: empresa.cnpj || '', telefone: empresa.telefone || '', nome_responsavel: empresa.nome_responsavel || '', endereco: empresa.endereco || '', status: 'ativo' }, { headers: { Authorization: `Bearer ${token}` } });
                setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            }
            fetchEmpresas();
        } catch (error) {
            setToast({ header: 'Ops!', body: error?.response?.data?.message || 'Erro ao executar ação.', type: 'toast-error' });
        }
        setShowToast(true);
    };

    const abrirExcluir = (empresa) => {
        setConfirmacaoNome('');
        setShowExcluir({ show: true, empresa, etapa: 1 });
    };

    const fecharExcluir = () => {
        setShowExcluir({ show: false, empresa: null, etapa: 1 });
        setConfirmacaoNome('');
    };

    const executarExclusao = async () => {
        const { empresa } = showExcluir;
        if (!empresa || confirmacaoNome !== empresa.nome) return;
        setExcluindo(true);
        try {
            const response = await Api.post(
                `api/empresas/${empresa.id}/excluir`,
                { confirmacao_nome: confirmacaoNome },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            fecharExcluir();
            fetchEmpresas();
        } catch (error) {
            setToast({ header: 'Ops!', body: error?.response?.data?.message || 'Erro ao excluir a empresa.', type: 'toast-error' });
        }
        setExcluindo(false);
        setShowToast(true);
    };

    const abrirCriarAdmin = (empresa) => {
        resetAdmin({ nome_completo: '', codigo: '', email: '', senha: '' });
        setShowSenha(false);
        setShowModalAdmin({ show: true, empresa });
    };

    const onSubmitAdmin = async (data) => {
        try {
            const response = await Api.post(
                `api/empresas/${showModalAdmin.empresa.id}/criar-admin`,
                data,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            setShowModalAdmin({ show: false, empresa: null });
        } catch (error) {
            setToast({ header: 'Ops!', body: error?.response?.data?.message || 'Erro ao criar administrador.', type: 'toast-error' });
        }
        setShowToast(true);
    };

    const entrarNaEmpresa = async (empresa) => {
        try {
            const response = await Api.post('api/selecionar-empresa', { empresa_id: empresa.id }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            entrarNoContextoEmpresa(response.data, empresa.nome, '/dashboard');
        } catch (error) {
            setToast({ header: 'Ops!', body: error?.response?.data?.message || 'Não foi possível entrar na empresa.', type: 'toast-error' });
            setShowToast(true);
        }
    };

    const empresasFiltradas = empresas.filter(e =>
        !filtro ||
        e.nome.toLowerCase().includes(filtro.toLowerCase()) ||
        (e.cnpj || '').includes(filtro)
    );

    return (
        <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center mb-4 gap-2">
                <div>
                    <h4 className="mb-0 fw-bold"><i className="bi bi-buildings me-2 text-primary" />Empresas</h4>
                    <small className="text-muted">Gerencie as empresas da plataforma</small>
                </div>
                <button className="btn btn-primary flex-shrink-0" onClick={abrirNova}>
                    <i className="bi bi-plus-lg me-sm-1" /><span className="d-none d-sm-inline">Nova empresa</span>
                </button>
            </div>

            {/* Cards de estatísticas — compactos e lado a lado no mobile */}
            <div className="row g-2 g-md-3 mb-4">
                <div className="col-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body d-flex flex-column flex-md-row align-items-center gap-1 gap-md-3 p-2 p-md-3 text-center text-md-start">
                            <div className="rounded-3 p-2 p-md-3" style={{ background: '#e8f0fe' }}>
                                <i className="bi bi-buildings fs-4 text-primary" />
                            </div>
                            <div>
                                <div className="text-muted small">Total<span className="d-none d-md-inline"> de Empresas</span></div>
                                <div className="fs-4 fs-md-2 fw-bold lh-1">{totalEmpresas}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body d-flex flex-column flex-md-row align-items-center gap-1 gap-md-3 p-2 p-md-3 text-center text-md-start">
                            <div className="rounded-3 p-2 p-md-3" style={{ background: '#e6f9f0' }}>
                                <i className="bi bi-building-check fs-4 text-success" />
                            </div>
                            <div>
                                <div className="text-muted small">Ativas</div>
                                <div className="fs-4 fs-md-2 fw-bold lh-1 text-success">{ativas}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body d-flex flex-column flex-md-row align-items-center gap-1 gap-md-3 p-2 p-md-3 text-center text-md-start">
                            <div className="rounded-3 p-2 p-md-3" style={{ background: '#f5f5f5' }}>
                                <i className="bi bi-building-slash fs-4 text-secondary" />
                            </div>
                            <div>
                                <div className="text-muted small">Inativas</div>
                                <div className="fs-4 fs-md-2 fw-bold lh-1 text-secondary">{inativas}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabela principal */}
            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                    <div className="input-group" style={{ maxWidth: 340 }}>
                        <span className="input-group-text bg-white border-end-0">
                            <i className="bi bi-search text-muted" />
                        </span>
                        <input
                            className="form-control border-start-0"
                            placeholder="Buscar por nome ou CNPJ..."
                            value={filtro}
                            onChange={e => setFiltro(e.target.value)}
                        />
                    </div>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table align-middle mb-0 table-striped table-hover">
                            <thead>
                                <tr>
                                    <th colSpan="4" className="bg-primary text-white rounded-top">Lista de Empresas</th>
                                </tr>
                                <tr className="border border-white text-nowrap">
                                    <th className="text-uppercase text-black-50 fw-bold fs-8 bg-muted">Nome</th>
                                    <th className="text-uppercase text-black-50 fw-bold fs-8 bg-muted d-none d-md-table-cell">CNPJ</th>
                                    <th className="text-uppercase text-black-50 fw-bold fs-8 bg-muted">Status</th>
                                    <th className="text-uppercase text-black-50 fw-bold fs-8 bg-muted text-end pe-4">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {empresasFiltradas.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center text-muted py-5">
                                            <i className="bi bi-building-slash fs-2 d-block mb-2" />
                                            {filtro ? 'Nenhuma empresa encontrada para esta busca.' : 'Nenhuma empresa cadastrada.'}
                                        </td>
                                    </tr>
                                )}
                                {empresasFiltradas.map((empresa) => (
                                    <tr key={empresa.id}>
                                        <td>
                                            <span className="fw-semibold">{empresa.nome}</span>
                                            {String(empresa.id) === String(empresaAtual) && (
                                                <span className="badge bg-info ms-2">Atual</span>
                                            )}
                                        </td>
                                        <td className="text-muted d-none d-md-table-cell">{empresa.cnpj || '-'}</td>
                                        <td>
                                            <span className={`badge ${empresa.status === 'ativo' ? 'bg-success' : 'bg-secondary'}`}>
                                                {empresa.status === 'ativo' ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </td>
                                        <td className="text-end pe-3">
                                            <div className="d-inline-flex align-items-center gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary text-nowrap"
                                                    onClick={() => entrarNaEmpresa(empresa)}
                                                    disabled={empresa.status !== 'ativo'}
                                                >
                                                    <i className="bi bi-box-arrow-in-right me-sm-1" /><span className="d-none d-sm-inline">Entrar</span>
                                                </button>
                                                <DropdownMenu
                                                    align="end"
                                                    items={[
                                                    {
                                                        icon: 'graph-up',
                                                        label: 'Ver dashboard',
                                                        onClick: () => navigate(`/empresa/${empresa.id}/dashboard`),
                                                    },
                                                    { divider: true },
                                                    {
                                                        icon: 'pencil',
                                                        label: 'Editar',
                                                        onClick: () => abrirEdicao(empresa),
                                                    },
                                                    {
                                                        icon: 'person-plus',
                                                        label: 'Criar administrador',
                                                        onClick: () => abrirCriarAdmin(empresa),
                                                    },
                                                    { divider: true },
                                                    empresa.status === 'ativo'
                                                        ? {
                                                            icon: 'slash-circle',
                                                            label: 'Inativar',
                                                            onClick: () => confirmarAcao(empresa, 'inativar'),
                                                        }
                                                        : {
                                                            icon: 'check-circle',
                                                            label: 'Ativar',
                                                            onClick: () => confirmarAcao(empresa, 'ativar'),
                                                        },
                                                    { divider: true },
                                                    {
                                                        icon: 'trash',
                                                        label: 'Excluir definitivamente',
                                                        className: 'text-danger',
                                                        onClick: () => abrirExcluir(empresa),
                                                    },
                                                    ]}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th colSpan="4" className="bg-primary text-white rounded-bottom">
                                        <div className="table-footer d-flex justify-content-between align-items-center">
                                            <span>
                                                {filtro
                                                    ? `Exibindo ${empresasFiltradas.length} de ${totalEmpresas} registros`
                                                    : `${totalEmpresas} empresa${totalEmpresas !== 1 ? 's' : ''} — ${ativas} ativa${ativas !== 1 ? 's' : ''}${inativas > 0 ? `, ${inativas} inativa${inativas !== 1 ? 's' : ''}` : ''}`
                                                }
                                            </span>
                                        </div>
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal criar/editar */}
            <Modal show={showModal.show} onHide={() => setShowModal({ show: false, empresa: null })} backdrop="static" size="lg">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Header closeButton>
                        <span className="fw-semibold fs-5 text-primary">
                            {showModal.empresa ? 'Editar empresa' : 'Nova empresa'}
                        </span>
                    </Modal.Header>
                    <Modal.Body className="bg-gray-100 p-4">
                        <div className="container">
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">Nome *</label>
                                    <input
                                        className={`form-control border-new-light ${errors.nome ? 'is-invalid' : ''}`}
                                        placeholder="Nome da empresa"
                                        {...register('nome', { required: 'O nome é obrigatório.' })}
                                    />
                                    {errors.nome && <div className="invalid-feedback">{errors.nome.message}</div>}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label text-primary fw-medium">CNPJ</label>
                                    <input
                                        className="form-control border-new-light"
                                        placeholder="00.000.000/0000-00"
                                        {...register('cnpj', {
                                            onChange: (e) => { e.target.value = maskCNPJ(e.target.value); }
                                        })}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label text-primary fw-medium">Telefone</label>
                                    <input
                                        className="form-control border-new-light"
                                        placeholder="(00) 00000-0000"
                                        {...register('telefone', {
                                            onChange: (e) => { e.target.value = maskTelefone(e.target.value); }
                                        })}
                                    />
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">Nome do responsável</label>
                                    <input
                                        className="form-control border-new-light"
                                        placeholder="Nome do responsável pela empresa"
                                        {...register('nome_responsavel')}
                                    />
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">Endereço</label>
                                    <input
                                        className="form-control border-new-light"
                                        placeholder="Rua, número, bairro, cidade - UF"
                                        {...register('endereco')}
                                    />
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">Status</label>
                                    <Controller
                                        name="status"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                options={statusOptions}
                                                styles={customStyles}
                                                placeholder="Selecione o status"
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-end">
                        <div className="d-flex justify-content-end">
                            <button type="button" className="btn me-2"
                                onClick={() => setShowModal({ show: false, empresa: null })}>
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary">Salvar</button>
                        </div>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* Modal de confirmação */}
            <Modal show={showConfirm.show} onHide={() => setShowConfirm({ show: false, empresa: null, acao: null })} centered>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">
                        {showConfirm.acao === 'inativar' ? 'Inativar empresa' : 'Ativar empresa'}
                    </span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <div className="container">
                        {showConfirm.acao === 'inativar' ? (
                            <p className="mb-0">
                                Tem certeza que deseja <strong>inativar</strong> a empresa{' '}
                                <strong>"{showConfirm.empresa?.nome}"</strong>?
                                <br />
                                <span className="text-muted small">
                                    Usuários desta empresa não conseguirão fazer login enquanto ela estiver inativa.
                                </span>
                            </p>
                        ) : (
                            <p className="mb-0">
                                Tem certeza que deseja <strong>ativar</strong> a empresa{' '}
                                <strong>"{showConfirm.empresa?.nome}"</strong>?
                            </p>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn me-2"
                            onClick={() => setShowConfirm({ show: false, empresa: null, acao: null })}>
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className={showConfirm.acao === 'inativar' ? 'btn btn-danger' : 'btn btn-primary'}
                            onClick={executarAcao}
                        >
                            {showConfirm.acao === 'inativar' ? 'Inativar' : 'Ativar'}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Modal de exclusão definitiva — dupla confirmação */}
            <Modal size='lg' show={showExcluir.show} onHide={fecharExcluir} backdrop="static" centered>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-danger">
                        <i className="bi bi-exclamation-triangle-fill me-2" />
                        Excluir empresa definitivamente
                    </span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    {showExcluir.etapa === 1 ? (
                        <div>
                            <p>
                                Você está prestes a excluir <strong>definitivamente</strong> a empresa{' '}
                                <strong>"{showExcluir.empresa?.nome}"</strong>.
                            </p>
                            <div className="alert alert-danger mb-0">
                                <strong>Esta ação não pode ser desfeita.</strong> Serão apagados permanentemente:
                                <ul className="mb-0 mt-2">
                                    <li>Todos os usuários da empresa (admins, gestores, RH e alunos)</li>
                                    <li>Todos os cursos, módulos, aulas e avaliações</li>
                                    <li>Todos os certificados emitidos e o histórico de progresso</li>
                                    <li>Agendamentos, práticas registradas, relatórios e logs</li>
                                    <li>Arquivos enviados (fotos, PDFs, listas de presença, assinatura digital)</li>
                                </ul>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <p>
                                Para confirmar, digite o nome exato da empresa:{' '}
                                <strong className="user-select-all">{showExcluir.empresa?.nome}</strong>
                            </p>
                            <input
                                className="form-control border-danger"
                                placeholder="Digite o nome da empresa"
                                value={confirmacaoNome}
                                onChange={e => setConfirmacaoNome(e.target.value)}
                                autoFocus
                            />
                            <small className="text-muted">
                                A exclusão só será executada se o nome digitado for idêntico.
                            </small>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn me-2" onClick={fecharExcluir} disabled={excluindo}>
                            Cancelar
                        </button>
                        {showExcluir.etapa === 1 ? (
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => setShowExcluir(prev => ({ ...prev, etapa: 2 }))}
                            >
                                Entendo os riscos, continuar
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-danger"
                                disabled={confirmacaoNome !== showExcluir.empresa?.nome || excluindo}
                                onClick={executarExclusao}
                            >
                                {excluindo ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Excluindo...</>
                                ) : (
                                    <><i className="bi bi-trash me-1" />Excluir definitivamente</>
                                )}
                            </button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Modal criar administrador */}
            <Modal show={showModalAdmin.show} onHide={() => setShowModalAdmin({ show: false, empresa: null })} backdrop="static" size="lg">
                <form onSubmit={handleSubmitAdmin(onSubmitAdmin)}>
                    <Modal.Header closeButton>
                        <span className="fw-semibold fs-5 text-primary">
                            Criar administrador — {showModalAdmin.empresa?.nome}
                        </span>
                    </Modal.Header>
                    <Modal.Body className="bg-gray-100 p-4">
                        <div className="container">
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">Nome completo *</label>
                                    <input
                                        className={`form-control border-new-light ${errorsAdmin.nome_completo ? 'is-invalid' : ''}`}
                                        placeholder="Nome do administrador"
                                        {...registerAdmin('nome_completo', { required: 'O nome é obrigatório.' })}
                                    />
                                    {errorsAdmin.nome_completo && <div className="invalid-feedback">{errorsAdmin.nome_completo.message}</div>}
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">Matrícula *</label>
                                    <input
                                        className={`form-control border-new-light ${errorsAdmin.codigo ? 'is-invalid' : ''}`}
                                        placeholder="Login de acesso"
                                        {...registerAdmin('codigo', { required: 'A matrícula é obrigatória.' })}
                                    />
                                    {errorsAdmin.codigo && <div className="invalid-feedback">{errorsAdmin.codigo.message}</div>}
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">E-mail *</label>
                                    <input
                                        type="email"
                                        className={`form-control border-new-light ${errorsAdmin.email ? 'is-invalid' : ''}`}
                                        placeholder="email@empresa.com"
                                        {...registerAdmin('email', {
                                            required: 'O e-mail é obrigatório.',
                                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido.' }
                                        })}
                                    />
                                    {errorsAdmin.email && <div className="invalid-feedback">{errorsAdmin.email.message}</div>}
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">Senha *</label>
                                    <InputGroup>
                                        <input
                                            type={showSenha ? 'text' : 'password'}
                                            className={`form-control border-new-light ${errorsAdmin.senha ? 'is-invalid' : ''}`}
                                            placeholder="Mínimo 6 caracteres"
                                            {...registerAdmin('senha', {
                                                required: 'A senha é obrigatória.',
                                                minLength: { value: 6, message: 'A senha deve ter pelo menos 6 caracteres.' }
                                            })}
                                        />
                                        <button type="button" className="btn btn-outline-secondary"
                                            onClick={() => setShowSenha(v => !v)}>
                                            <i className={`bi bi-eye${showSenha ? '-slash' : ''}`} />
                                        </button>
                                        {errorsAdmin.senha && <div className="invalid-feedback">{errorsAdmin.senha.message}</div>}
                                    </InputGroup>
                                    <small className="text-muted">O administrador deverá redefinir a senha no primeiro acesso.</small>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-end">
                        <div className="d-flex justify-content-end">
                            <button type="button" className="btn me-2"
                                onClick={() => setShowModalAdmin({ show: false, empresa: null })}>
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary">Criar administrador</button>
                        </div>
                    </Modal.Footer>
                </form>
            </Modal>

            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={toast.type}
                style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}
            >
                <Toast.Header><strong className="me-auto">{toast.header}</strong></Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>
        </div>
    );
}

export default ListaEmpresas;
